import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Tooltip from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import Helper from "../../Helper";
import { getTrans as t } from "../../Localization/translations";
import { useSubscriptionFeatures } from "../../contexts/SubscriptionContext";


export default function AddManualAccount() {
    const navigate = useNavigate();

    const [manual, setManual] = useState({
        entry_date: new Date(),
        amount: "",
        description: "",
        debit_account: "",
        credit_account: "",
        reference: "",
        status: "New",
        attachment: null,
    });

    const handleAccountingDate = (value) => {
        setManual({
            ...manual,
            entry_date: value,
        });
    };



    const [allocations, setAllocations] = useState([
        { contract: null, asset: null, customer: null, amount: "", assetOptions: [], customerOptions: [] }
    ]);
    const { planFeatures } = useSubscriptionFeatures();

    const [allocationError, setAllocationError] = useState("");

    const [subcompanyOptions, setSubcompanyOptions] = useState([]);
    const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState([]);

    const [subcompany, setSubcompany] = useState("");
    const [lineOfBusiness, setLineOfBusiness] = useState("");

    const [accountOptions, setAccountOptions] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);



    /* ---------------- CONTRACT LOADER ---------------- */
    const loadContractOptions = async (inputValue) => {
        try {
            const searchTerm = inputValue === "" ? "%" : inputValue;
            const res = await axios.get(API + `contracts/contractsactive/filter/?id=${searchTerm}`, {
                headers: authHeader(),
            });
            const data = res.data.data || res.data.results || res.data;
            return Array.isArray(data) ? data : [];
        } catch (err) {
            return [];
        }
    };

    // const fetchAllAccounts = async () => {
    //     let url = API + "accounting/accounts/";
    //     let all = [];

    //     while (url) {
    //         const res = await axios.get(url, { headers: authHeader() });
    //         all = [...all, ...(res.data.data || [])];
    //         url = res.data.links?.next;
    //     }

    //     const formatted = all.map(acc => ({
    //         value: acc.id,
    //         label: `${acc.account_number} - ${acc.account_code}`,
    //     }));

    //     setAccountOptions(formatted);
    // };



    const fetchAllAccounts = async () => {
        let url = API + "accounting/accounts/";
        let all = [];

        // Remember the protocol we started with
        const baseProtocol = new URL(url).protocol; // "http:" or "https:"

        while (url) {
            const res = await axios.get(url, { headers: authHeader() });
            all = [...all, ...(res.data.data || [])];

            let next = res.data.links?.next;

            if (next) {
                // Force the next link to use the SAME protocol as the original request
                try {
                    const nextUrl = new URL(next, url); // resolves relative URLs too
                    nextUrl.protocol = baseProtocol;
                    next = nextUrl.toString();
                } catch (err) {
                    console.warn("Invalid next URL:", next, err);
                    next = null; // stop pagination on bad link
                }
            }

            url = next;
        }

        const formatted = all
            .filter(acc => acc.manual === "Y")   // ✅ ONLY MANUAL YES
            .map(acc => ({
                value: acc.id,
                label: `${acc.account_number} - ${acc.account_code}`,
            }));


        setAccountOptions(formatted);
    };



        const isApprovalDisabled = planFeatures !== null && planFeatures.approvals === false;


    useEffect(() => {
        fetchAllAccounts();
    }, []);




    /* ---------------- ASSET LOADER ---------------- */
    const fetchAssetsForContract = async (contractId, rowIndex) => {
        try {
            const res = await axios.get(API + `contracts/asset-view/${contractId}/`, {
                headers: authHeader(),
            });

            const data = res.data.data || res.data.results || res.data;

            setAllocations(prev =>
                prev.map((row, i) =>
                    i === rowIndex ? { ...row, assetOptions: data } : row
                )
            );
        } catch (err) {
            console.error(err);
        }
    };

    /* ---------------- GRID CHANGE ---------------- */
    const handleGridChange = (index, field, value) => {
        if (field === "contract") {
            const selected = value;

            setAllocations(prev => prev.map((row, i) => {
                if (i !== index) return row;

                if (selected) {
                    fetchAssetsForContract(selected.id, index);

                    const customerObj = selected.party
                        ? { label: selected.customer_name, value: selected.party }
                        : null;

                    return {
                        ...row,
                        contract: selected,
                        asset: null,
                        customer: customerObj,
                        customerOptions: customerObj ? [customerObj] : [],
                        assetOptions: []
                    };
                }

                return { contract: null, asset: null, customer: null, amount: "", assetOptions: [], customerOptions: [] };
            }));
            return;
        }

        setAllocations(prev =>
            prev.map((row, i) =>
                i === index ? { ...row, [field]: value } : row
            )
        );
    };

    const fetchSubCompanies = async () => {
        try {
            const res = await axios.get(API + "company/subcompanies/", {
                headers: authHeader(),
            });

            const data = res.data.data || res.data;

            if (Array.isArray(data)) {
                setSubcompanyOptions(data);

                // 🔹 Find default company
                const defaultCompany = data.find(c => c.default === true || c.is_default === true);

                if (defaultCompany) {
                    setSubcompany(defaultCompany.id);
                }
            }

        } catch (err) {
            console.error("Error loading subcompanies", err);
        }
    };

    const fetchLineOfBusiness = async () => {
        try {
            const res = await axios.get(API + "company/lobs/", {
                headers: authHeader(),
            });

            const data = res.data.data || res.data;

            if (Array.isArray(data)) {
                setLineOfBusinessOptions(data);

                // 🔹 Find default LOB
                const defaultLob = data.find(lob => lob.default === true || lob.is_default === true);

                if (defaultLob) {
                    setLineOfBusiness(defaultLob.id);
                }
            }
        } catch (err) {
            console.error("Error loading LOB", err);
        }
    };

    useEffect(() => {
        fetchSubCompanies();
        fetchLineOfBusiness();
    }, []);


    const addNewRow = () => {
        setAllocations([...allocations, { contract: null, asset: null, customer: null, amount: "", assetOptions: [], customerOptions: [] }]);
    };

    const deleteRow = (index) => {
        setAllocations(prev => prev.filter((_, i) => i !== index));
    };

    const totalAllocated = allocations.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

    /* ---------------- FORM CHANGE ---------------- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setManual({ ...manual, [name]: value });
    };

    const handleFileChange = (e) => {
        setManual({ ...manual, attachment: e.target.files[0] });
    };

    /* ---------------- SAVE ---------------- */
    const handleSave = async () => {

        // Required field validations
        if (!subcompany) {
            toast.error("Company is required");
            return;
        }


        if (!manual.entry_date) {
            toast.error("Accounting Date is required");
            return;
        }

        if (!manual.amount || parseFloat(manual.amount) <= 0) {
            toast.error("Amount is required");
            return;
        }

        if (!manual.debit_account) {
            toast.error("Debit Account is required");
            return;
        }

        if (!manual.credit_account) {
            toast.error("Credit Account is required");
            return;
        }

        if (manual.debit_account === manual.credit_account) {
            toast.error("Debit and Credit account cannot be same");
            return;
        }

        const amount = parseFloat(manual.amount || 0);

        // remove empty rows (optional safety)
        const validAllocations = allocations.filter(
            row => row.amount && parseFloat(row.amount) > 0
        );

        const totalAllocated = validAllocations.reduce(
            (sum, r) => sum + (parseFloat(r.amount) || 0),
            0
        );

        // ✅ Only check when allocations exist
        if (validAllocations.length > 0 && totalAllocated > amount) {
            toast.error("Allocation total cannot be greater than Amount");
            return;
        }

        const payloadAllocations = validAllocations.map(row => ({
            contract: row.contract?.id || null,
            asset: row.asset?.id || null,
            customer: row.customer?.value || null,
            amount: row.amount
        }));

        const payload = {
            ...manual,
            entry_date: Helper.DatePicker(manual.entry_date),
            subcompany: subcompany || null,
            line_of_business: lineOfBusiness || null,
            allocations: payloadAllocations   // can be empty []
        };

        try {
            await axios.post(API + "manualaccounting/", payload, {
                headers: authHeader()
            });

            toast.success("Manual Accounting Entry Saved");
            setTimeout(() => navigate("/transactions"), 1500);
        } catch (err) {
            console.error(err.response?.data);
            toast.error("Failed to save entry");
        }
    };

    return (
        <div className="page-wrapper">
            <ToastContainer />

            <div className="content container-fluid">
                <div className="header pb-0 mb-3 pt-3">
                    <div>
                        <h5>{t("Manual Accounting")}</h5>
                        <h6>
                            <Link to="/manualaccounting" className="tc">
                                {t("Manual Accounting")}
                            </Link>{" "}
                            / <span> {t("New Accounting")} </span>
                        </h6>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="success"></div>

                                <form>
                                    <div className="row">

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Company ")}
                                                    <Tooltip content={t("Subcompany_Tooltip")}>
                                                        <i className="fa fa-light fa-circle-info"></i>
                                                    </Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <select
                                                        className="form-select form-control"
                                                        value={subcompany}
                                                        onChange={(e) => setSubcompany(e.target.value)}
                                                    >
                                                        <option value="">Select Company</option>
                                                        {subcompanyOptions.map((sub) => (
                                                            <option key={sub.id} value={sub.id}>
                                                                {sub.sub_company_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="col-form-label">
                                                {t("Line of Business")}
                                                <Tooltip content={t("Lineofbusiness_Tooltip")}>
                                                    <i className="fa fa-light fa-circle-info"></i>
                                                </Tooltip>
                                            </label>
                                            <select
                                                className="form-select form-control"
                                                value={lineOfBusiness}
                                                onChange={(e) => setLineOfBusiness(e.target.value)}
                                            >
                                                <option value="">Select Line of Business</option>
                                                {lineOfBusinessOptions.map((lob) => (
                                                    <option key={lob.id} value={lob.id}>
                                                        {lob.lob_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Accounting Date")}
                                                    <Tooltip content={t("Date of Manual Accounting")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <DatePicker
                                                        className="form-control datepick"
                                                        selected={manual.entry_date}
                                                        onChange={(e) => handleAccountingDate(e)}
                                                        name="entry_date"
                                                        placeholderText={"mm/dd/yyyy"}
                                                        showYearDropdown
                                                        scrollableYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                    />

                                                </div>
                                            </div>
                                        </div>


                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Amount")}
                                                    <Tooltip content={t("Amount_Tooltip")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <input type="number" name="amount" className="form-control" value={manual.amount} onChange={handleChange} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Debit Account")}
                                                    <Tooltip content={t("Amount_Tooltip")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <Select
                                                        value={accountOptions.find(a => a.value === manual.debit_account) || null}
                                                        options={accountOptions}
                                                        onChange={(selected) =>
                                                            setManual({ ...manual, debit_account: selected?.value || null })
                                                        }
                                                        getOptionLabel={(option) => option.label}
                                                        getOptionValue={(option) => option.value}
                                                        placeholder={accountLoading ? "Loading..." : "Select Debit Account"}
                                                        isClearable
                                                    />


                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label required">
                                                    {t("Credit Account")}
                                                    <Tooltip content={t("Amount_Tooltip")}><i className="fa fa-light fa-circle-info ms-1"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <Select
                                                        value={accountOptions.find(a => a.value === manual.credit_account) || null}
                                                        options={accountOptions}
                                                        onChange={(selected) =>
                                                            setManual({ ...manual, credit_account: selected?.value || null })
                                                        }
                                                        getOptionLabel={(option) => option.label}
                                                        getOptionValue={(option) => option.value}
                                                        placeholder={accountLoading ? "Loading..." : "Select Credit Account"}
                                                        isClearable
                                                    />


                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label">
                                                    {t("Reference")}{" "}
                                                    <Tooltip content={t("Reference")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <input type="text" name="reference" className="form-control" value={manual.reference} onChange={handleChange} />
                                                </div>
                                            </div>
                                        </div>


                                        <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label">
                                                    {t("Description")}{" "}
                                                    <Tooltip content={t("Description_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <textarea name="description" className="form-control" rows="3" value={manual.description} onChange={handleChange}></textarea>
                                                </div>
                                            </div>
                                        </div>

                                        {/* <div className="col-md-6">
                                            <div className="form-group row">
                                                <label className="col-lg-12 col-form-label">
                                                    {t("Attachments")}{" "}
                                                    <Tooltip content={t("Attachments")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                                                </label>
                                                <div className="col-lg-12">
                                                    <input type="file" className="form-control" onChange={handleFileChange} />
                                                </div>
                                            </div>
                                        </div> */}
                                    </div>
                                </form>

                                {/* <div>

                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }}>
                                        <div className="row pt-3">
                                            <div className="col-xl-6"><label className="col-form-label">{t("Items")}</label></div>
                                            <div className="col-xl-6">
                                                <div className="text-right">
                                                    <button type="button" className="btn btn-outline-success" onClick={addNewRow}>+ {t("ADD")}</button>
                                                </div>
                                            </div>
                                        </div>

                                        <table className="table table-nowrap mb-0">
                                            <thead style={{ color: 'black' }}>
                                                <tr>
                                                    <th className="col-form-label" style={{ minWidth: "200px", color: 'black' }}>
                                                        {t("Contract")}
                                                        <Tooltip content={t("Contract Number")}>
                                                            <i className="fa fa-light fa-circle-info ms-1"></i>
                                                        </Tooltip>
                                                    </th>
                                                    <th className="col-form-label" style={{ minWidth: "200px", color: 'black' }}>
                                                        {t("Asset")}
                                                        <Tooltip content={t("Asset Name")}>
                                                            <i className="fa fa-light fa-circle-info ms-1"></i>
                                                        </Tooltip>
                                                    </th>
                                                    <th className="col-form-label" style={{ minWidth: "200px", color: 'black' }}>
                                                        {t("Customer")}
                                                        <Tooltip content={t("Customer Name")}>
                                                            <i className="fa fa-light fa-circle-info ms-1"></i>
                                                        </Tooltip>
                                                    </th>
                                                    <th className="col-form-label" style={{ minWidth: "150px", color: 'black' }}>
                                                        {t("Amount")}
                                                        <Tooltip content={t("Allocated Amount")}>
                                                            <i className="fa fa-light fa-circle-info ms-1"></i>
                                                        </Tooltip>
                                                    </th>

                                                    <th className="col-form-label" style={{ width: "50px" }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allocations.map((row, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <AsyncSelect
                                                                cacheOptions
                                                                defaultOptions
                                                                value={row.contract}
                                                                loadOptions={loadContractOptions}
                                                                onChange={(val) => handleGridChange(index, "contract", val)}
                                                                getOptionLabel={(o) => o.contract_number}
                                                                getOptionValue={(o) => o.id}
                                                                isClearable
                                                            />
                                                        </td>
                                                        <td>
                                                            <Select
                                                                value={row.asset}
                                                                options={row.assetOptions}
                                                                onChange={(val) => handleGridChange(index, "asset", val)}
                                                                isDisabled={!row.contract}
                                                                getOptionLabel={(o) => o.asset_desc || o.asset_number}
                                                                getOptionValue={(o) => o.id}
                                                                isClearable
                                                            />
                                                        </td>
                                                        <td>
                                                            <Select
                                                                value={row.customer}
                                                                options={row.customerOptions}
                                                                onChange={(val) => handleGridChange(index, "customer", val)}
                                                                isDisabled={!row.contract}
                                                                isClearable
                                                            />
                                                        </td>
                                                        <td>
                                                            <input type="number" className="form-control"
                                                                value={row.amount}
                                                                isDisabled={!row.contract}
                                                                onChange={(e) => handleGridChange(index, "amount", e.target.value)} />
                                                        </td>
                                                        <td>
                                                            <button className="btn btn-outline-danger" onClick={() => deleteRow(index)}>x</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="3"></td>
                                                    <td className="fw-bold text-end">{totalAllocated.toFixed(2)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div> */}

                                <div className="d-flex align-items-start py-3">
                                    <button className="btn btn-secondary" onClick={() => navigate("/transactions")}>CANCEl</button>
                                    &nbsp;
                                    <button className="btn btn-primary" onClick={handleSave}>Save</button>
                                    {!isApprovalDisabled && (
                                    <div style={{ marginLeft: "15px", fontSize: "13px", color: "#6c757d" }}>
                                        This transaction needs to be submitted for approval.
                                        <br />
                                        Please navigate to the view screen to submit for approval.
                                    </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
